import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Stack,
  Typography,
  MenuItem,
  TextField,
  InputAdornment,
  Divider,
} from '@mui/material';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';

export default function FilterSelect({
  filters,
  selectedFilters,
  setFilters,
  group,
  setSelectedFilters,
}) {
  // hooks
  const popover = usePopover();

  // states
  const [state, setState] = useState('unactive');
  const [count, setCount] = useState(0);
  const [filterSearchText, setFilterSearchText] = useState('');

  // functions
  const handleSearchFilter = e => {
    const { value } = e.target;
    setFilterSearchText(value);
  };

  const handleSelectedFilters = code => {
    setSelectedFilters(prevSelectedFilters => {
      if (prevSelectedFilters.includes(code)) {
        return prevSelectedFilters.filter(filter => filter !== code);
      }
      return [...prevSelectedFilters, code];
    });
  };

  const getFilteredItems = items => {
    const filteredItems = items.filter(item =>
      item.name.toLowerCase().includes(filterSearchText.toLowerCase()),
    );
    return filteredItems;
  };

  const sortBySelectedAndAlphabetically = items => {
    const selectedItems = items.filter(item =>
      selectedFilters.includes(item.code),
    );

    const unselectedItems = items.filter(
      item => !selectedFilters.includes(item.code),
    );

    const sortedSelectedItems = selectedItems.sort((a, b) =>
      a.name.localeCompare(b.name),
    );

    const sortedUnselectedItems = unselectedItems.sort((a, b) =>
      a.name.localeCompare(b.name),
    );

    return [...sortedSelectedItems, ...sortedUnselectedItems];
  };

  // effects
  useEffect(() => {
    let _count = 0;

    selectedFilters.forEach(selectedFilter => {
      const _filter = filters.find(filter => filter.code === selectedFilter);
      if (_filter) {
        _count += 1;
      }
    });

    setCount(_count);
  }, [filters, group, selectedFilters]);

  useEffect(() => {
    if (
      selectedFilters.some(selectedFilter =>
        filters.find(filter => filter.code === selectedFilter),
      )
    ) {
      setState('selected');
    } else if (popover.open) {
      setState('active');
    } else {
      setState('unactive');
    }
  }, [filters, group, selectedFilters]);

  const SearchBar = (
    <TextField
      id="filter-search"
      variant="standard"
      placeholder="Search..."
      value={filterSearchText}
      onChange={handleSearchFilter}
      sx={{
        '& .MuiInput-input': {
          minWidth: 0,
        },
        '& .MuiInput-root': {
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
        },
      }}
      InputProps={{
        disableUnderline: true,
        startAdornment: (
          <InputAdornment position="start">
            <Iconify
              sx={{
                color: theme => theme.palette.content.primaryBlack3,
              }}
              icon="tabler:search"
            />
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <>
      <Box
        onClick={event => {
          popover.onOpen(event);
          setState('active');
        }}
        sx={{
          cursor: 'pointer',
          borderRadius: '8px',
          color: `content.${
            state === 'selected' ? 'primaryWhite' : 'primaryBlack2'
          }`,
          backgroundColor: `background.${
            state === 'selected' ? 'primaryAccent1' : 'primaryWhite1'
          }`,
          border: theme =>
            `1px solid ${
              theme.palette.border[
                state === 'selected' || state === 'active'
                  ? 'primaryAccent1'
                  : 'primaryBlack3'
              ]
            }`,
          '&:hover': {
            backgroundColor: theme =>
              `${
                theme.palette.background[
                  `${state === 'selected' ? 'primaryAccent1' : 'primaryGrey1'}`
                ]
              }`,
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing="4px"
          padding="6px 16px"
          height="100%"
          width="100%"
        >
          <Typography variant="input">{group.split('_').join(' ')}</Typography>
          <Typography variant="input" visibility={count ? 'visible' : 'hidden'}>
            {count}
          </Typography>
        </Stack>
      </Box>
      <CustomPopover
        open={popover.open}
        onClose={() => {
          popover.onClose();
          if (state === 'active') setState('unactive');

          const _filters = sortBySelectedAndAlphabetically(filters);

          setFilters(prevFilters => ({
            ...prevFilters,
            [group]: _filters,
          }));
        }}
        sx={{ width: 300, padding: 0 }}
      >
        {SearchBar}
        <Divider />
        <Scrollbar
          sx={{
            minHeight: '200px',
            maxHeight: '320px',
          }}
        >
          <Stack
            padding="8px"
            sx={{
              minHeight: '200px',
              maxHeight: '320px',
            }}
          >
            {getFilteredItems(filters).length ? (
              getFilteredItems(filters).map(filter => {
                const isSelected = selectedFilters.includes(filter.code);
                return (
                  <MenuItem
                    key={filter.code}
                    value={filter.code}
                    sx={{
                      px: '12px',
                      py: '10px',
                      background: theme =>
                        isSelected
                          ? theme.palette.background.primaryAccent2
                          : theme.palette.background.primaryWhite,
                      '&:hover': {
                        background: theme =>
                          isSelected
                            ? theme.palette.background.primaryAccent2
                            : theme.palette.background.primaryGrey1,
                      },
                    }}
                    onClick={() => handleSelectedFilters(filter.code)}
                  >
                    <Stack
                      alignItems="center"
                      direction="row"
                      width="100%"
                      justifyContent="space-between"
                    >
                      <Typography
                        width="80%"
                        variant="caption"
                        color="content.primaryBlack2"
                        whiteSpace="wrap"
                        maxWidth="200px"
                      >
                        {filter.name}
                      </Typography>
                      {isSelected && (
                        <Stack width="10%">
                          <Iconify
                            sx={{
                              color: 'background.primaryAccent1',
                            }}
                            icon="tabler:check"
                          />
                        </Stack>
                      )}
                    </Stack>
                  </MenuItem>
                );
              })
            ) : (
              <Stack
                width="100%"
                height="100%"
                alignItems="center"
                justifyContent="center"
                minHeight="200px"
                maxHeight="320px"
              >
                <Typography variant="caption" color="content.primaryBlack3">
                  No {group.split('_').join(' ').toLowerCase()} found
                </Typography>
              </Stack>
            )}
          </Stack>
        </Scrollbar>
      </CustomPopover>
    </>
  );
}

FilterSelect.propTypes = {
  filters: PropTypes.array,
  group: PropTypes.string,
  selectedFilters: PropTypes.array,
  setSelectedFilters: PropTypes.func,
  setFilters: PropTypes.func,
};
